import { useState, useEffect, useRef } from 'react';
import { PolyguardClient } from '@polyguard/sdk';
import { getIntegrationSnippet, getSnippetLabels, INTEGRATION_TYPES, INTEGRATION_TYPE_TO_INDEX, INDEX_TO_INTEGRATION_TYPE } from './integrationSnippets';
import { getApiServers, PROOF_TYPES } from './constants';
import CodeBlockWithCopy from './CodeBlockWithCopy';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ThemeProvider, CssBaseline, Box, Typography, GridLegacy as Grid, Card, TextField, FormControl, FormControlLabel, Checkbox, MenuItem, Select, Tabs, Tab, InputLabel, FormGroup, Button, Chip } from '@mui/material';
import SideNav, { drawerWidth } from './SideNav';
import theme from './theme';
// Intercom initialized in SideNav

export default function SandboxPage() {
    
    // State and logic from AuthParams
    const defaultApiServer = process.env.REACT_APP_API_SERVER || 
      (process.env.NODE_ENV === 'production' ? 'api-global.polyguard.ai' : 'local-api.actual.video');
  
    const [appId, setAppId] = useState("sandbox");

    // Initialize API server from localStorage or default
    const [apiServer, setApiServer] = useState(() => {
      const savedApiServer = localStorage.getItem('polyguard_api_server');
      return savedApiServer || defaultApiServer;
    });
    const [jwtData, setJwtData] = useState(null);
  
    const [customProof, setCustomProof] = useState('');
    const [requiredProofs, setRequiredProofs] = useState(['pg_attestation_key_id', 'pg_presence']);
    const [integrationType, setIntegrationType] = useState('websocket');
    // Example extra config states for each integration type
    const [redirectUrl, setRedirectUrl] = useState('/callback');
    const [callbackUrl, setCallbackUrl] = useState('/webhook');
    const [cookieName, setCookieName] = useState('polyguard_jwt');
    const leftCardRef = useRef(null);
    const [leftCardHeight, setLeftCardHeight] = useState(null);
    const [snippet, setSnippet] = useState({ code: '', language: '', label: '' });
    const [language, setLanguage] = useState('node');
  
  
    const handleProofChange = (proof) => {
      setRequiredProofs(prev => {
        const newProofs = prev.includes(proof)
          ? prev.filter(p => p !== proof)
          : [...prev, proof];
        return newProofs;
      });
    };

    const handleAddCustomProof = () => {
      if (customProof.trim() && !requiredProofs.includes(customProof.trim())) {
        setRequiredProofs(prev => [...prev, customProof.trim()]);
        setCustomProof('');
      }
    };

    const handleRemoveCustomProof = (proof) => {
      // Check if this proof is not in the predefined PROOF_TYPES
      const isPredefinedProof = PROOF_TYPES.some(p => p.type === proof);
      if (!isPredefinedProof) {
        setRequiredProofs(prev => prev.filter(p => p !== proof));
      }
    };

    // Helper function to check if photo verification should be enabled
    const isPhotoVerificationEnabled = () => {
      const hasName = requiredProofs.includes('name');
      const hasAge = requiredProofs.includes('pg_age');
      const hasCustomProof = requiredProofs.some(proof => 
        !PROOF_TYPES.some(p => p.type === proof)
      );
      return hasName || hasAge || hasCustomProof;
    };

    // Clear photo verification when conditions are no longer met
    useEffect(() => {
      const hasName = requiredProofs.includes('name');
      const hasAge = requiredProofs.includes('pg_age');
      const hasCustomProof = requiredProofs.some(proof => 
        !PROOF_TYPES.some(p => p.type === proof)
      );
      const isEnabled = hasName || hasAge || hasCustomProof;
      
      if (!isEnabled && requiredProofs.includes('pg_photo_verification_url')) {
        setRequiredProofs(prev => prev.filter(p => p !== 'pg_photo_verification_url'));
      }
    }, [requiredProofs]);

    const handleApiServerChange = (newApiServer) => {
      setApiServer(newApiServer);
      localStorage.setItem('polyguard_api_server', newApiServer);
    };
  
    useEffect(() => {
      function updateHeight() {
        if (leftCardRef.current) {
          setLeftCardHeight(leftCardRef.current.offsetHeight);
        }
      }
      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }, [apiServer, appId, integrationType, redirectUrl, callbackUrl, cookieName, requiredProofs]);
  
    useEffect(() => {
      let cancelled = false;
      async function loadSnippet() {
        const result = await getIntegrationSnippet(integrationType, {
          appId,
          apiServer,
          callbackUrl,
          cookieName,
          redirectUrl,
          requiredProofs,
          language,
        });
        if (!cancelled) setSnippet(result);
      }
      loadSnippet();
      return () => { cancelled = true; };
    }, [integrationType, appId, apiServer, callbackUrl, cookieName, redirectUrl, requiredProofs, language]);
  
    // Remove all modal/qr logic. Use SDK for modal.
    // HTTP routes through /api/polyguard (Vercel Edge proxy) so the API key
    // stays server-side. The apiServer dropdown still drives the WebSocket
    // upgrade, which is ticket-authenticated.
    const handlePromptToVerify = async () => {
      setJwtData(null);
      const client = new PolyguardClient({
        integrationType,
        appId,
        proxyUrl: '/api/polyguard',
        apiServer,
        requiredProofs,
        scanType: requiredProofs.includes('pg_presence') ? 'multi' : 'single',
        redirectUrl,
        callbackUrl,
        cookieName,
      });
      try {
        const jwt = await client.verify();
        setJwtData(jwt);
      } catch (err) {
        console.error(err);
        // Optionally handle error/cancel (e.g., show a toast)
      }
    };
  
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: "flex" }}>
          {/* Navigation Sidebar */}
          <SideNav paperBg="#fafafa" titleColor="#1976d2" />

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: `calc(100% - ${drawerWidth}px)`,
            }}
          >
            <Box className="App" sx={{ pb: 8 }}>
              <Grid
                container
                spacing={4}
                justifyContent="start"
                sx={{ maxWidth: 1200, margin: "0 auto" }}
              >
                <Grid item xs={12}>
                  <Typography variant="h1" sx={{ textAlign: "left" }}>
                    Polyguard SDK Sandbox
                  </Typography>
                </Grid>

                {/* Params (Left) */}
                <Grid item xs={12} md={4}>
                  <Card elevation={2} sx={{ height: "100%" }} ref={leftCardRef}>
                    <Typography variant="h2" gutterBottom>
                      Set Parameters
                    </Typography>
                    <form onSubmit={(e) => e.preventDefault()}>
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <label style={{ flex: 1 }}>
                          API Server:
                          <select
                            value={apiServer}
                            onChange={(e) => handleApiServerChange(e.target.value)}
                            style={{ width: "100%" }}
                          >
                            {getApiServers().map((server) => (
                              <option key={server} value={server}>{server}</option>
                            ))}
                          </select>
                        </label>
                        <label style={{ width: 140, marginLeft: 8 }}>
                          App ID:
                          <input
                            type="text"
                            value={appId}
                            onChange={(e) => setAppId(e.target.value)}
                            style={{ width: "100%" }}
                          />
                        </label>
                      </Box>
                      {/* Integration Type */}
                      <Box sx={{ mb: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel id="integration-type-label">
                            Integration Type
                          </InputLabel>
                          <Select
                            labelId="integration-type-label"
                            value={integrationType}
                            label="Integration Type"
                            onChange={(e) => setIntegrationType(e.target.value)}
                          >
                            <MenuItem value="websocket">WebSocket</MenuItem>
                            <MenuItem value="url" disabled={true}>
                              URL Redirect (GET)
                            </MenuItem>
                            {/* <MenuItem value="callback">Webhook (POST)</MenuItem> */}
                            {/* <MenuItem value="cookie">Cookie</MenuItem> */}
                          </Select>
                        </FormControl>
                      </Box>
                      {/* Conditional Integration Options */}
                      {integrationType === "url" && (
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            label="Redirect URL"
                            value={redirectUrl}
                            onChange={(e) => setRedirectUrl(e.target.value)}
                            disabled={true}
                            size="small"
                          />
                        </Box>
                      )}
                      {integrationType === "callback" && (
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            label="Callback URL"
                            value={callbackUrl}
                            onChange={(e) => setCallbackUrl(e.target.value)}
                            size="small"
                          />
                        </Box>
                      )}
                      {integrationType === "cookie" && (
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            label="Cookie Name"
                            value={cookieName}
                            onChange={(e) => setCookieName(e.target.value)}
                            size="small"
                          />
                        </Box>
                      )}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1">
                          Required Proofs for Login:
                        </Typography>
                        <FormGroup>
                          {PROOF_TYPES.map((proof) => (
                            <FormControlLabel
                              key={proof.type}
                              control={
                                <Checkbox
                                  checked={requiredProofs.includes(proof.type)}
                                  onChange={() => handleProofChange(proof.type)}
                                  disabled={
                                    proof.disabled || 
                                    (proof.type === 'pg_photo_verification_url' && !isPhotoVerificationEnabled())
                                  }
                                />
                              }
                              label={proof.label}
                            />
                          ))}
                        </FormGroup>

                        {/* Custom Proof Input */}
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: 500, textAlign: "left" }}
                          >
                            Importable Proof:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              size="small"
                              placeholder="e.g. custom_claim"
                              value={customProof}
                              onChange={(e) => setCustomProof(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddCustomProof();
                                }
                              }}
                              sx={{ flex: 1 }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleAddCustomProof}
                              disabled={!customProof.trim()}
                              sx={{ height: "40px" }}
                            >
                              Add
                            </Button>
                          </Box>

                          {/* Display custom proofs as chips */}
                          {requiredProofs.filter(
                            (proof) =>
                              !PROOF_TYPES.some((p) => p.type === proof)
                          ).length > 0 && (
                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {requiredProofs
                                .filter(
                                  (proof) =>
                                    !PROOF_TYPES.some((p) => p.type === proof)
                                )
                                .map((proof) => (
                                  <Chip
                                    key={proof}
                                    label={proof}
                                    size="small"
                                    onDelete={() =>
                                      handleRemoveCustomProof(proof)
                                    }
                                  />
                                ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </form>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ borderRadius: 2, fontWeight: 600, mt: 2 }}
                      onClick={handlePromptToVerify}
                      // Always enabled, SDK handles modal state
                    >
                      Prompt to Verify
                    </Button>
                  </Card>
                </Grid>
                {/* JWT (Right) */}
                <Grid item xs={12} md={7}>
                  <Card
                    elevation={2}
                    sx={leftCardHeight ? { height: leftCardHeight } : {}}
                  >
                    <Typography variant="h2" gutterBottom>
                      JWT Data
                    </Typography>
                    {jwtData ? (
                      <Box
                        sx={{
                          textAlign: "left",
                          maxHeight: leftCardHeight
                            ? leftCardHeight - 56
                            : "100%",
                          overflow: "auto",
                        }}
                      >
                        <CodeBlockWithCopy
                          code={
                            typeof jwtData === "string"
                              ? JSON.stringify(JSON.parse(jwtData), null, 2)
                              : JSON.stringify(jwtData, null, 2)
                          }
                          language="json"
                          theme={atomDark}
                        />
                      </Box>
                    ) : (
                      <Typography color="text.secondary" sx={{ mt: 4 }}>
                        No JWT yet
                      </Typography>
                    )}
                  </Card>
                </Grid>
                <Grid item xs={12} md={11}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h2" gutterBottom sx={{ mb: 0 }}>
                      Install this Polyguard Check
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="language-select-label">
                        Language
                      </InputLabel>
                      <Select
                        labelId="language-select-label"
                        value={language}
                        label="Language"
                        onChange={(e) => setLanguage(e.target.value)}
                      >
                        <MenuItem value="node">Node</MenuItem>
                        <MenuItem value="javascript">Javascript</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Tabs
                    value={INTEGRATION_TYPE_TO_INDEX[integrationType]}
                    onChange={(_, newIndex) =>
                      setIntegrationType(INDEX_TO_INTEGRATION_TYPE[newIndex])
                    }
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="Integration Type Tabs"
                    sx={{ mb: 2 }}
                  >
                    {INTEGRATION_TYPES.map((type) => (
                      <Tab key={type} label={getSnippetLabels()[type]} />
                    ))}
                  </Tabs>
                  <div style={{ width: "100%", margin: 0, textAlign: "left" }}>
                    {(() => {
                      return (
                        <CodeBlockWithCopy
                          code={snippet.code}
                          language={snippet.language}
                          theme={atomDark}
                        />
                      );
                    })()}
                  </div>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }
  